import { getBackendUrl } from '../utils/api';
import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MapPin, Radio, User, MessageSquare, AlertCircle, CheckCircle, Paperclip, X } from "lucide-react";
import axios from "axios";
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = getBackendUrl();

const ContactForm = () => {
  const { t, currentLang } = useLanguage();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    stationName: '',
    frequency: '',      // Combined frequency like "89.5"
    frequencyMain: '',  // Main part: 88-108
    frequencyDecimal: '', // Decimal part: 0-9
    region: '',
    phone: '',
    email: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [attachedFile, setAttachedFile] = useState(null);
  const [fileError, setFileError] = useState('');

  // Region keys for translation
  const regionKeys = [
    'regionCyprus',  // Κύπρος - FIRST
    'regionAttica', 'regionThessaloniki', 'regionCentralMacedonia', 'regionWestMacedonia',
    'regionEastMacedoniaThrace', 'regionEpirus', 'regionThessaly', 'regionIonianIslands',
    'regionWestGreece', 'regionCentralGreece', 'regionPeloponnese', 'regionNorthAegean',
    'regionSouthAegean', 'regionCrete'
  ];

  const validateBusinessEmail = (email) => {
    const freeProviders = ['gmail.com', 'hotmail.com', 'yahoo.com', 'outlook.com', 
                           'live.com', 'icloud.com', 'aol.com', 'protonmail.com',
                           'zoho.com', 'yandex.com', 'mail.com'];
    
    const domain = email.split('@')[1]?.toLowerCase();
    if (freeProviders.includes(domain)) {
      return false;
    }
    return true;
  };

  const validateForm = () => {
    const newErrors = {};

    // First Name - Real name validation
    if (!formData.firstName.trim()) {
      newErrors.firstName = t('nameRequired');
    } else {
      const name = formData.firstName.trim();
      if (name === name.toUpperCase() && name.length > 2) {
        newErrors.firstName = t('enterRealName');
      }
      if (/(.)\1{2,}/.test(name) || /^[Α-Ω]{2,}[Σ]$/.test(name)) {
        newErrors.firstName = t('enterValidName');
      }
      if (name.length < 2) {
        newErrors.firstName = t('minChars');
      }
    }

    // Last Name - Real name validation
    if (!formData.lastName.trim()) {
      newErrors.lastName = t('lastNameRequired');
    } else {
      const name = formData.lastName.trim();
      if (name === name.toUpperCase() && name.length > 2) {
        newErrors.lastName = t('enterRealLastName');
      }
      if (name.length < 2) {
        newErrors.lastName = t('minChars');
      }
    }

    // Station Name
    if (!formData.stationName.trim()) {
      newErrors.stationName = t('stationRequired');
    }

    // Region
    if (!formData.region) {
      newErrors.region = t('regionRequired');
    }

    // Phone - Must be exactly 10 digits
    if (!formData.phone.trim()) {
      newErrors.phone = t('phoneRequired');
    } else {
      const cleanPhone = formData.phone.replace(/[\s\-()]/g, '');
      if (!/^[0-9]{10}$/.test(cleanPhone)) {
        newErrors.phone = t('invalidPhone');
      }
    }

    // FM Frequency - Required
    if (!formData.frequencyMain || !formData.frequencyDecimal === undefined) {
      newErrors.frequency = currentLang === 'el' ? 'Η συχνότητα είναι υποχρεωτική' : 'Frequency is required';
    }

    // Email
    if (!formData.email.trim()) {
      newErrors.email = t('emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('invalidEmail');
    } else if (!validateBusinessEmail(formData.email)) {
      newErrors.email = t('businessEmailRequired');
    }

    // Message
    if (!formData.message.trim()) {
      newErrors.message = t('messageRequired');
    } else if (formData.message.trim().length < 10) {
      newErrors.message = t('minMessageLength');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFileError('');
    
    if (file) {
      // Validate file size (max 10MB)
      const maxSize = 10 * 1024 * 1024; // 10MB in bytes
      if (file.size > maxSize) {
        setFileError('Το αρχείο είναι πολύ μεγάλο. Μέγιστο μέγεθος: 10MB');
        setAttachedFile(null);
        return;
      }
      
      // Validate file type (images, pdf, doc, docx, txt)
      const allowedTypes = [
        'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'text/plain'
      ];
      
      if (!allowedTypes.includes(file.type)) {
        setFileError(t('invalidFileType'));
        setAttachedFile(null);
        return;
      }
      
      setAttachedFile(file);
    }
  };

  const removeFile = () => {
    setAttachedFile(null);
    setFileError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrorMessage('');
    setSuccess(false);

    try {
      // Create FormData for file upload
      const formDataToSend = new FormData();
      formDataToSend.append('firstName', formData.firstName);
      formDataToSend.append('lastName', formData.lastName);
      formDataToSend.append('stationName', formData.stationName);
      formDataToSend.append('frequency', formData.frequency);  // FM Frequency
      formDataToSend.append('region', formData.region);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('message', formData.message);
      
      // Add file if attached
      if (attachedFile) {
        formDataToSend.append('attachment', attachedFile);
      }
      
      const response = await axios.post(`${BACKEND_URL}/api/contact`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (response.data.success) {
        setSuccess(true);
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          stationName: '',
          frequency: '',
          frequencyMain: '',
          frequencyDecimal: '',
          region: '',
          phone: '',
          email: '',
          message: ''
        });
        setAttachedFile(null);
        
        // Hide success message after 15 seconds (increased for better visibility)
        setTimeout(() => setSuccess(false), 15000);
      }
    } catch (error) {
      console.error('Contact form error:', error);
      setErrorMessage(error.response?.data?.detail || t('messageError'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="w-full max-w-4xl mx-auto mt-8 shadow-lg border-2 border-blue-200">
      <CardHeader className="bg-gradient-to-r from-[#2D0C4B] to-[#4A1D7A] text-white">
        <CardTitle className="text-3xl flex items-center gap-2">
          <Mail className="w-8 h-8" />
          {t('contactTitle')}
        </CardTitle>
        <CardDescription className="text-blue-100">
          {t('contactDesc')}
        </CardDescription>
      </CardHeader>
      <CardContent className="p-6">
        {success && (
          <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center gap-3">
            <CheckCircle className="w-6 h-6 text-green-600" />
            <div>
              <p className="font-semibold text-green-800">{t('messageSent')}</p>
            </div>
          </div>
        )}

        {errorMessage && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <p className="text-red-800">{errorMessage}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* First Name & Last Name */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                <User className="w-4 h-4" />
                {t('firstName')} *
              </label>
              <input
                type="text"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 ${
                  errors.firstName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={currentLang === 'el' ? 'π.χ. Γιώργος' : 'e.g. John'}
              />
              {errors.firstName && (
                <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                <User className="w-4 h-4" />
                {t('lastName')} *
              </label>
              <input
                type="text"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 ${
                  errors.lastName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={currentLang === 'el' ? 'π.χ. Παπαδόπουλος' : 'e.g. Smith'}
              />
              {errors.lastName && (
                <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Station Name & Region */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                <Radio className="w-4 h-4" />
                {t('stationName')} *
              </label>
              <input
                type="text"
                value={formData.stationName}
                onChange={(e) => handleInputChange('stationName', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 ${
                  errors.stationName ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={currentLang === 'el' ? 'π.χ. Radio Solutions FM' : 'e.g. Radio Solutions FM'}
              />
              {errors.stationName && (
                <p className="text-red-500 text-sm mt-1">{errors.stationName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                {t('region')} *
              </label>
              <select
                value={formData.region}
                onChange={(e) => handleInputChange('region', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                  errors.region ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
              >
                <option value="">{t('selectRegion')}</option>
                {regionKeys.map(key => (
                  <option key={key} value={t(key)}>{t(key)}</option>
                ))}
              </select>
              {errors.region && (
                <p className="text-red-500 text-sm mt-1">{errors.region}</p>
              )}
            </div>
          </div>

          {/* Frequency FM */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
                <Radio className="w-4 h-4" />
                {currentLang === 'el' ? 'Συχνότητα FM' : 'FM Frequency'} *
              </label>
              <div className="flex items-center gap-2">
                <select
                  value={formData.frequencyMain}
                  onChange={(e) => {
                    handleInputChange('frequencyMain', e.target.value);
                    // Auto-combine frequency
                    const decimal = formData.frequencyDecimal || '0';
                    if (e.target.value) {
                      handleInputChange('frequency', `${e.target.value},${decimal}`);
                    }
                  }}
                  className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    errors.frequency ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <option value="">MHz</option>
                  {Array.from({ length: 21 }, (_, i) => 88 + i).map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <span className="text-xl font-bold text-gray-600 dark:text-gray-300">,</span>
                <select
                  value={formData.frequencyDecimal}
                  onChange={(e) => {
                    handleInputChange('frequencyDecimal', e.target.value);
                    // Auto-combine frequency
                    const main = formData.frequencyMain || '';
                    if (main) {
                      handleInputChange('frequency', `${main},${e.target.value || '0'}`);
                    }
                  }}
                  className={`w-20 px-3 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                    errors.frequency ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                  }`}
                >
                  <option value="">-</option>
                  {Array.from({ length: 10 }, (_, i) => i).map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
                <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">FM</span>
              </div>
              {formData.frequency && (
                <p className="text-sm text-blue-600 dark:text-blue-400 mt-1">
                  📻 {formData.frequency} FM
                </p>
              )}
              {errors.frequency && (
                <p className="text-red-500 text-sm mt-1">{errors.frequency}</p>
              )}
            </div>
            
            {/* Empty space or additional field placeholder */}
            <div className="hidden md:block"></div>
          </div>

          {/* Phone & Email */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Phone className="w-4 h-4" />
                {t('phone')} *
              </label>
              <input
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white text-gray-900 ${
                  errors.phone ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder={currentLang === 'el' ? 'π.χ. 210 1234567' : 'e.g. 210 1234567'}
              />
              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">{errors.phone}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
                <Mail className="w-4 h-4" />
                {t('email')} *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 ${
                  errors.email ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
                }`}
                placeholder={currentLang === 'el' ? 'π.χ. info@radiofm.gr' : 'e.g. info@radiofm.com'}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-1">{errors.email}</p>
              )}
            </div>
          </div>

          {/* Message */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
              <MessageSquare className="w-4 h-4" />
              {t('message')} *
            </label>
            <textarea
              value={formData.message}
              onChange={(e) => handleInputChange('message', e.target.value)}
              rows={6}
              className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 resize-none ${
                errors.message ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
              }`}
              placeholder={currentLang === 'el' ? 'Γράψτε το μήνυμά σας εδώ...' : 'Write your message here...'}
            />
            {errors.message && (
              <p className="text-red-500 text-sm mt-1">{errors.message}</p>
            )}
          </div>

          {/* File Attachment */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2 flex items-center gap-2">
              <Paperclip className="w-4 h-4" />
              {t('attachFile')}
            </label>
            
            {!attachedFile ? (
              <div className="relative">
                <input
                  type="file"
                  id="file-upload"
                  onChange={handleFileChange}
                  className="hidden"
                  accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,application/pdf,.doc,.docx,.txt"
                />
                <label
                  htmlFor="file-upload"
                  className="flex items-center justify-center w-full px-4 py-3 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="text-center">
                    <Paperclip className="w-8 h-8 mx-auto text-gray-400 mb-2" />
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      <span className="font-semibold text-blue-600 dark:text-blue-400">{t('upload')}</span>
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      JPG, PNG, GIF, PDF, DOC, DOCX, TXT (max 10MB)
                    </p>
                  </div>
                </label>
              </div>
            ) : (
              <div className="flex items-center justify-between p-4 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                <div className="flex items-center gap-3">
                  <Paperclip className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-800 dark:text-white">{attachedFile.name}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {(attachedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={removeFile}
                  className="p-1 hover:bg-red-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-red-600" />
                </button>
              </div>
            )}
            
            {fileError && (
              <p className="text-red-500 text-sm mt-2 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {fileError}
              </p>
            )}
          </div>

          {/* Submit Button */}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-[#2D0C4B] to-[#4A1D7A] hover:from-[#4A1D7A] hover:to-[#6B3FA0] text-white font-semibold py-3 text-lg"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                {t('sending')}
              </>
            ) : (
              <>
                <Mail className="w-5 h-5 mr-2" />
                {t('sendMessage')}
              </>
            )}
          </Button>

          <p className="text-xs text-gray-500 text-center mt-2">
            * {t('requiredField')}
          </p>
        </form>
      </CardContent>
    </Card>
  );
};

export default ContactForm;
